import { supabase } from '../lib/supabase';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';

export interface AporteAnalysis {
  miembro_id: string;
  nombres: string;
  apellidos: string;
  rut_pasaporte: string;
  aporte_mensual: number;
  aporte_cuota_iglesia: number;
  modo_pago_cuota_iglesia?: string;
  total_aportes_mensuales_realizados: number;
  total_cuota_iglesia_realizada: number;
  total_cuota_iglesia_jal: number;
  total_cuota_iglesia_pa: number;
  ultimo_aporte_fecha?: string;
  meses_sin_aportar: number;
  aportio_este_mes: boolean;
  racha_aporte_mensual: number;
  racha_cuota_iglesia: number;
}

export interface AporteSummary {
  total_miembros: number;
  total_aporte_mensual_esperado: number;
  total_cuota_iglesia_esperada: number;
  total_cuota_iglesia_jal_esperada: number;
  total_cuota_iglesia_pa_esperada: number;
  total_aportes_mensuales_recibidos: number;
  total_cuota_iglesia_recibida: number;
  total_cuota_iglesia_jal: number;
  total_cuota_iglesia_pa: number;
  total_adultos: number;
  total_ninos: number;
  promedio_aporte_miembro: number;
  miembros_sin_aportar_2_meses: number;
  porcentaje_concentracion_top20: number;
  miembros_al_dia: number;
  miembros_en_mora: number;
  /** Miembros con derechos que registraron un aporte mensual en el período. */
  aportantes_aporte_mensual: number;
  /** Miembros con derechos que registraron cuota iglesia en el período. */
  aportantes_cuota_iglesia: number;
}

export interface AporteMesSnapshot {
  mes: number;
  año: number;
  miembros_aportan: number;
}

export interface AporteFilters {
  busqueda?: string;
  estadoPago?: 'al_dia' | 'mora' | 'todos';
  modoPago?: string;
  tipoMiembro?: 'adulto' | 'niño';
  ordenarPor?: 'nombre' | 'monto' | 'ultimo_aporte' | 'racha';
}

/** Determina qué mes mostrar: si estamos en los primeros 5 días del mes, muestra el anterior. */
interface MiembroDelResumen {
  readonly id: string;
  readonly estado_miembro: string;
}

interface MovimientoDeAporte {
  readonly origen_id: string;
  readonly categoria_aporte: string | null;
}

/**
 * Separa el conjunto de miembros en los dos usos distintos que tiene el resumen de aportes.
 *
 * - `conDerechos` responde *"cuántos son / cuánto deberían aportar"*: conteos, compromisos,
 *   morosidad y concentración. Quien perdió sus derechos ya no participa y no compromete nada.
 * - `idsParaMovimientos` incluye a **todos**, también a quienes perdieron sus derechos: el dinero
 *   que aportaron mientras participaban entró de verdad y no puede desaparecer de los totales
 *   recibidos.
 *
 * **No mover este filtro a la consulta de la base.** Esa misma consulta decide de qué personas se
 * traen los movimientos, así que filtrarla en el origen haría desaparecer plata realmente recibida.
 */
export function separarMiembrosDelResumen<T extends MiembroDelResumen>(
  miembros: readonly T[]
): { conDerechos: T[]; idsParaMovimientos: string[] } {
  return {
    conDerechos: miembros.filter(m => m.estado_miembro === 'activo'),
    idsParaMovimientos: miembros.map(m => m.id),
  };
}

/**
 * Cuenta personas distintas que aportaron en el período, restringido a miembros con derechos.
 *
 * Devuelve las dos cifras que muestra la ficha "Aportantes", una por categoría. No suma un total:
 * quien pagó ambas cosas cuenta en las dos, y sumarlas lo contaría dos veces.
 *
 * **`aporteMensual` es la cifra de "aportó al lumisial"**, y de ella sale `miembros_al_dia`. La
 * cuota iglesia no entra ahí: es dinero de paso hacia la diócesis, y en el lumisial "aportes"
 * significa siempre aportes **para** el lumisial. Quien solo pagó su cuota iglesia no aportó al
 * lumisial y por lo tanto figura en mora — es el criterio deliberado, no un descuido.
 *
 * La restricción a miembros con derechos no es cosmética: `miembros_en_mora` se calcula como
 * `totalMiembros - aporteMensual`, así que si alguien sin derechos contara acá, quedaría negativa.
 *
 * Invariante del que la ficha depende: todo ingreso de miembro se crea con `categoria_aporte`
 * 'aporte_mensual' o 'cuota_iglesia', nunca sin categoría — el CHECK de la columna admite solo esos
 * dos valores y ningún punto de creación la deja sin definir. Si algún día se agrega una tercera
 * categoría, quedaría invisible en ambas cifras sin dar error.
 */
export function contarAportantes(
  miembrosConDerechos: readonly MiembroDelResumen[],
  movimientosDelPeriodo: readonly MovimientoDeAporte[]
): { aporteMensual: number; cuotaIglesia: number } {
  const idsConDerechos = new Set(miembrosConDerechos.map(m => m.id));
  const deMiembroConDerechos = movimientosDelPeriodo.filter(m => idsConDerechos.has(m.origen_id));

  const contarDistintos = (movs: readonly MovimientoDeAporte[]) =>
    new Set(movs.map(m => m.origen_id)).size;

  return {
    aporteMensual: contarDistintos(deMiembroConDerechos.filter(m => m.categoria_aporte === 'aporte_mensual')),
    cuotaIglesia: contarDistintos(deMiembroConDerechos.filter(m => m.categoria_aporte === 'cuota_iglesia')),
  };
}

export function getDisplayPeriod(): { mes: number; año: number } {
  const hoy = new Date();
  if (hoy.getDate() <= 5) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    return { mes: d.getMonth() + 1, año: d.getFullYear() };
  }
  return { mes: hoy.getMonth() + 1, año: hoy.getFullYear() };
}


function calcularRachaAportes(
  aportes: Array<{ categoria_aporte: string; fecha: string }>,
  categoria: string,
  inicioMes: number,
  inicioAño: number
): number {
  let racha = 0;
  let checkMes = inicioMes;
  let checkAño = inicioAño;
  for (let i = 0; i < 36; i++) {
    const inicio = fechaInicioMes(checkMes, checkAño);
    const fin = fechaFinMes(checkMes, checkAño);
    if (!aportes.some(a => a.categoria_aporte === categoria && a.fecha >= inicio && a.fecha <= fin)) break;
    racha++;
    if (checkMes === 1) { checkMes = 12; checkAño--; }
    else { checkMes--; }
  }
  return racha;
}

class AporteService {
  async getAnalysisByLumisial(
    lumisialId: string,
    filters?: AporteFilters,
    /** Período a evaluar. Si se omite, la regla propia de la pantalla de Aportes. */
    periodo?: { mes: number; año: number }
  ): Promise<AporteAnalysis[]> {
    try {
      let query = supabase
        .from('miembros')
        .select(`
          id,
          nombres,
          apellidos,
          rut_pasaporte,
          aporte_mensual,
          aporte_cuota_iglesia,
          modo_pago_cuota_iglesia,
          tipo_miembro
        `)
        .eq('lumisial_actual_id', lumisialId)
        .eq('estado_miembro', 'activo');

      if (filters?.busqueda) {
        query = query.or(
          `nombres.ilike.%${filters.busqueda}%,apellidos.ilike.%${filters.busqueda}%,rut_pasaporte.ilike.%${filters.busqueda}%`
        );
      }

      if (filters?.tipoMiembro) {
        query = query.eq('tipo_miembro', filters.tipoMiembro);
      }

      if (filters?.modoPago) {
        query = query.eq('modo_pago_cuota_iglesia', filters.modoPago);
      }

      const { data: miembros, error: miembrosError } = await query;

      if (miembrosError) throw miembrosError;
      if (!miembros) return [];

      const miembroIds = miembros.map(m => m.id);

      const { data: movimientos, error: movimientosError } = await supabase
        .from('movimientos')
        .select('origen_id, monto, fecha, categoria_aporte, modo_pago_cuota_iglesia')
        .eq('lumisial_id', lumisialId)
        .eq('tipo', 'ingreso')
        .eq('origen_tipo', 'miembro')
        .eq('anulado', false)
        .in('origen_id', miembroIds);

      if (movimientosError) throw movimientosError;

      const now = new Date();
      const displayPeriod = periodo ?? getDisplayPeriod();
      const inicioMesActual = fechaInicioMes(displayPeriod.mes, displayPeriod.año);
      const finMesActual = fechaFinMes(displayPeriod.mes, displayPeriod.año);

      const análisis: AporteAnalysis[] = miembros.map(miembro => {
        const aportesMiembro = movimientos?.filter(m => m.origen_id === miembro.id) || [];

        const totalAportesMensualesRealizados = aportesMiembro
          .filter(a => a.categoria_aporte === 'aporte_mensual')
          .reduce((sum, a) => sum + a.monto, 0);

        const movimientosCuota = aportesMiembro.filter(a => a.categoria_aporte === 'cuota_iglesia');
        const totalCuotaIglesiaRealizada = movimientosCuota.reduce((sum, a) => sum + a.monto, 0);
        const totalCuotaIglesiaJal = movimientosCuota
          .filter(a => a.modo_pago_cuota_iglesia == null)
          .reduce((sum, a) => sum + a.monto, 0);
        const totalCuotaIglesiaPA = movimientosCuota
          .filter(a => a.modo_pago_cuota_iglesia != null)
          .reduce((sum, a) => sum + a.monto, 0);

        let ultimoAporteFecha: string | undefined;
        // Sin valor inicial: las dos ramas de abajo lo asignan, y un 0 acá solo aparentaba ser
        // un valor por defecto que nunca se usa.
        let mesesSinAportar: number;

        if (aportesMiembro.length > 0) {
          const fechasOrdenadas = aportesMiembro
            .map(a => new Date(a.fecha))
            .sort((a, b) => b.getTime() - a.getTime());

          ultimoAporteFecha = fechasOrdenadas[0].toISOString().split('T')[0];

          const mesesDiff = Math.floor(
            (now.getTime() - fechasOrdenadas[0].getTime()) / (1000 * 60 * 60 * 24 * 30)
          );
          mesesSinAportar = Math.max(0, mesesDiff);
        } else {
          mesesSinAportar = 12;
        }

        // Aportó en el mes del período de visualización
        const aportioEsteMes = aportesMiembro.some(
          a => a.fecha >= inicioMesActual && a.fecha <= finMesActual
        );

        // Racha: empieza desde el mes anterior al período actual (el mes en curso aún no está cerrado)
        const rachaInicioMes = displayPeriod.mes === 1 ? 12 : displayPeriod.mes - 1;
        const rachaInicioAño = displayPeriod.mes === 1 ? displayPeriod.año - 1 : displayPeriod.año;

        // Racha aporte mensual y cuota iglesia: meses consecutivos hacia atrás desde el mes anterior
        const rachaAporteMensual = calcularRachaAportes(aportesMiembro, 'aporte_mensual', rachaInicioMes, rachaInicioAño);
        const rachaCuotaIglesia = calcularRachaAportes(aportesMiembro, 'cuota_iglesia', rachaInicioMes, rachaInicioAño);

        return {
          miembro_id: miembro.id,
          nombres: miembro.nombres,
          apellidos: miembro.apellidos,
          rut_pasaporte: miembro.rut_pasaporte,
          aporte_mensual: miembro.aporte_mensual,
          aporte_cuota_iglesia: miembro.aporte_cuota_iglesia,
          modo_pago_cuota_iglesia: miembro.modo_pago_cuota_iglesia,
          total_aportes_mensuales_realizados: totalAportesMensualesRealizados,
          total_cuota_iglesia_realizada: totalCuotaIglesiaRealizada,
          total_cuota_iglesia_jal: totalCuotaIglesiaJal,
          total_cuota_iglesia_pa: totalCuotaIglesiaPA,
          ultimo_aporte_fecha: ultimoAporteFecha,
          meses_sin_aportar: mesesSinAportar,
          aportio_este_mes: aportioEsteMes,
          racha_aporte_mensual: rachaAporteMensual,
          racha_cuota_iglesia: rachaCuotaIglesia
        };
      });

      let resultados = análisis;

      if (filters?.estadoPago === 'al_dia') {
        resultados = resultados.filter(a => a.aportio_este_mes);
      } else if (filters?.estadoPago === 'mora') {
        resultados = resultados.filter(a => !a.aportio_este_mes);
      }

      if (filters?.ordenarPor === 'nombre') {
        resultados.sort((a, b) => {
          const nombreA = `${a.apellidos} ${a.nombres}`.toLowerCase();
          const nombreB = `${b.apellidos} ${b.nombres}`.toLowerCase();
          return nombreA.localeCompare(nombreB);
        });
      } else if (filters?.ordenarPor === 'racha') {
        resultados.sort((a, b) => b.racha_aporte_mensual - a.racha_aporte_mensual);
      } else if (filters?.ordenarPor === 'ultimo_aporte') {
        resultados.sort((a, b) => {
          if (!a.ultimo_aporte_fecha) return 1;
          if (!b.ultimo_aporte_fecha) return -1;
          return new Date(b.ultimo_aporte_fecha).getTime() - new Date(a.ultimo_aporte_fecha).getTime();
        });
      }

      return resultados;
    } catch (error) {
      console.error('Error getting aportes analysis:', error);
      throw error;
    }
  }

  async getSummaryForMonth(lumisialId: string, mes: number, año: number): Promise<AporteSummary> {
    const inicio = fechaInicioMes(mes, año);
    const fin = fechaFinMes(mes, año);
    const prevMes = mes === 1 ? 12 : mes - 1;
    const prevAño = mes === 1 ? año - 1 : año;
    const inicioPrev = fechaInicioMes(prevMes, prevAño);

    const { data: miembros, error: miembrosError } = await supabase
      .from('miembros')
      .select('id, aporte_mensual, aporte_cuota_iglesia, modo_pago_cuota_iglesia, tipo_miembro, estado_miembro')
      .eq('lumisial_actual_id', lumisialId);

    if (miembrosError) throw miembrosError;

    const todosLosMiembros = miembros ?? [];
    if (todosLosMiembros.length === 0) {
      return {
        total_miembros: 0,
        total_aporte_mensual_esperado: 0,
        total_cuota_iglesia_esperada: 0,
        total_cuota_iglesia_jal_esperada: 0,
        total_cuota_iglesia_pa_esperada: 0,
        total_aportes_mensuales_recibidos: 0,
        total_cuota_iglesia_recibida: 0,
        total_cuota_iglesia_jal: 0,
        total_cuota_iglesia_pa: 0,
        total_adultos: 0,
        total_ninos: 0,
        promedio_aporte_miembro: 0,
        miembros_sin_aportar_2_meses: 0,
        porcentaje_concentracion_top20: 0,
        miembros_al_dia: 0,
        miembros_en_mora: 0,
        aportantes_aporte_mensual: 0,
        aportantes_cuota_iglesia: 0
      };
    }

    const { conDerechos: miembrosConDerechos, idsParaMovimientos: miembroIds } =
      separarMiembrosDelResumen(todosLosMiembros);
    const totalMiembros = miembrosConDerechos.length;

    // Query cubre mes actual + mes anterior para calcular silencio de 2 meses
    const { data: movimientos, error: movimientosError } = await supabase
      .from('movimientos')
      .select('origen_id, monto, categoria_aporte, modo_pago_cuota_iglesia, fecha')
      .eq('lumisial_id', lumisialId)
      .eq('tipo', 'ingreso')
      .eq('origen_tipo', 'miembro')
      .eq('anulado', false)
      .gte('fecha', inicioPrev)
      .lte('fecha', fin)
      .in('origen_id', miembroIds);

    if (movimientosError) throw movimientosError;

    const movsTodos = movimientos ?? [];
    const movs = movsTodos.filter(m => m.fecha >= inicio && m.fecha <= fin);
    const { aporteMensual: aportantesAporteMensual, cuotaIglesia: aportantesCuotaIglesia } =
      contarAportantes(miembrosConDerechos, movs);

    // Toda cifra de "aportes" del lumisial se calcula solo sobre el aporte mensual. La cuota
    // iglesia es dinero de paso hacia la diócesis y aparece únicamente en sus propias cifras
    // (`total_cuota_iglesia_*`, `aportantes_cuota_iglesia`), nunca mezclada en un total, un
    // promedio, una concentración ni un conteo de morosos.
    const movsAporteMensual = movs.filter(m => m.categoria_aporte === 'aporte_mensual');

    const totalAportesMensualesRecibidos = movs
      .filter(m => m.categoria_aporte === 'aporte_mensual')
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const movsCuota = movs.filter(m => m.categoria_aporte === 'cuota_iglesia');
    const totalCuotaIglesiaRecibida = movsCuota.reduce((sum, m) => sum + Number(m.monto), 0);
    const totalCuotaIglesiaJal = movsCuota
      .filter(m => m.modo_pago_cuota_iglesia == null)
      .reduce((sum, m) => sum + Number(m.monto), 0);
    const totalCuotaIglesiaPA = movsCuota
      .filter(m => m.modo_pago_cuota_iglesia != null)
      .reduce((sum, m) => sum + Number(m.monto), 0);

    const totalAporteMensualEsperado = miembrosConDerechos.reduce((sum, m) => sum + m.aporte_mensual, 0);
    const totalCuotaIglesiaEsperada = miembrosConDerechos.reduce((sum, m) => sum + m.aporte_cuota_iglesia, 0);
    const totalCuotaIglesiaJalEsperada = miembrosConDerechos
      .filter(m => m.modo_pago_cuota_iglesia == null)
      .reduce((sum, m) => sum + m.aporte_cuota_iglesia, 0);
    const totalCuotaIglesiaPAEsperada = miembrosConDerechos
      .filter(m => m.modo_pago_cuota_iglesia != null)
      .reduce((sum, m) => sum + m.aporte_cuota_iglesia, 0);
    const totalRecibido = totalAportesMensualesRecibidos;

    // Miembros sin aportar en los últimos 2 meses (mes actual + mes anterior). Solo cuenta el
    // aporte mensual: pagar la cuota iglesia no es aportar al lumisial.
    const miembrosConMovimiento2m = new Set<string>();
    for (const m of movsTodos) {
      if (m.categoria_aporte === 'aporte_mensual') miembrosConMovimiento2m.add(m.origen_id);
    }
    const miembrosSinAportar2Meses = miembrosConDerechos.filter(m => !miembrosConMovimiento2m.has(m.id)).length;

    // Concentración: % del total aportado por el top 20% de aportantes del mes.
    // El numerador se arma sobre TODOS los miembros, no solo los con derechos, porque el
    // denominador es `totalRecibido`, que incluye el dinero de quien perdió sus derechos durante
    // el período. Con poblaciones distintas la cifra miente: el aporte de un exmiembro quedaría
    // fuera de arriba y dentro de abajo, hundiendo el porcentaje —hasta 0% si fuera el único—.
    const aportePorMiembro = todosLosMiembros
      .map(m => movsAporteMensual.filter(mv => mv.origen_id === m.id).reduce((sum, mv) => sum + Number(mv.monto), 0))
      .sort((a, b) => b - a);
    const top20Count = Math.max(1, Math.ceil(aportePorMiembro.length * 0.2));
    const top20Total = aportePorMiembro.slice(0, top20Count).reduce((sum, v) => sum + v, 0);
    const porcentajeConcentracionTop20 = totalRecibido > 0 ? (top20Total / totalRecibido) * 100 : 0;
    const totalAdultos = miembrosConDerechos.filter(m => m.tipo_miembro === 'adulto').length;
    const totalNinos = miembrosConDerechos.filter(m => m.tipo_miembro === 'niño').length;

    return {
      total_miembros: totalMiembros,
      total_aporte_mensual_esperado: totalAporteMensualEsperado,
      total_cuota_iglesia_esperada: totalCuotaIglesiaEsperada,
      total_cuota_iglesia_jal_esperada: totalCuotaIglesiaJalEsperada,
      total_cuota_iglesia_pa_esperada: totalCuotaIglesiaPAEsperada,
      total_aportes_mensuales_recibidos: totalAportesMensualesRecibidos,
      total_cuota_iglesia_recibida: totalCuotaIglesiaRecibida,
      total_cuota_iglesia_jal: totalCuotaIglesiaJal,
      total_cuota_iglesia_pa: totalCuotaIglesiaPA,
      total_adultos: totalAdultos,
      total_ninos: totalNinos,
      promedio_aporte_miembro: totalMiembros > 0 ? totalRecibido / totalMiembros : 0,
      miembros_sin_aportar_2_meses: miembrosSinAportar2Meses,
      porcentaje_concentracion_top20: porcentajeConcentracionTop20,
      miembros_al_dia: aportantesAporteMensual,
      miembros_en_mora: totalMiembros - aportantesAporteMensual,
      aportantes_aporte_mensual: aportantesAporteMensual,
      aportantes_cuota_iglesia: aportantesCuotaIglesia
    };
  }

  /** Trae el conteo de miembros que aportaron en cada período. Una sola query para todos los períodos. */
  async getHistorialParticipacion(
    lumisialId: string,
    periodos: Array<{ mes: number; año: number }>
  ): Promise<AporteMesSnapshot[]> {
    if (periodos.length === 0) return [];

    // Rango total: desde el período más antiguo hasta el más reciente
    const oldest = periodos[periodos.length - 1];
    const newest = periodos[0];
    const rangoInicio = fechaInicioMes(oldest.mes, oldest.año);
    const rangoFin = fechaFinMes(newest.mes, newest.año);

    // El histórico se muestra junto a la ficha "Aportantes", que cuenta solo miembros con
    // derechos. Sin este filtro las dos cifras del mismo mes se contradicen en pantalla.
    const { data: activos, error: activosError } = await supabase
      .from('miembros')
      .select('id')
      .eq('lumisial_actual_id', lumisialId)
      .eq('estado_miembro', 'activo');

    if (activosError) throw activosError;

    const idsActivos = (activos ?? []).map(m => m.id);
    if (idsActivos.length === 0) {
      return periodos.map(({ mes, año }) => ({ mes, año, miembros_aportan: 0 }));
    }

    const { data: movimientos, error } = await supabase
      .from('movimientos')
      .select('origen_id, fecha')
      .eq('lumisial_id', lumisialId)
      .eq('tipo', 'ingreso')
      .eq('origen_tipo', 'miembro')
      // Solo el aporte mensual: la línea del histórico se lee como "cuántos aportaron", y en el
      // lumisial eso significa aportar al lumisial. Sin este filtro, quien solo pagó su cuota
      // iglesia inflaba la línea y contradecía la ficha "Aportantes" del mismo mes.
      .eq('categoria_aporte', 'aporte_mensual')
      .eq('anulado', false)
      .gte('fecha', rangoInicio)
      .lte('fecha', rangoFin)
      .in('origen_id', idsActivos);

    if (error) throw error;

    const movs = movimientos ?? [];

    return periodos.map(({ mes, año }) => {
      const inicio = fechaInicioMes(mes, año);
      const fin = fechaFinMes(mes, año);
      const movsMes = movs.filter(m => m.fecha >= inicio && m.fecha <= fin);
      const unicos = new Set(movsMes.map(m => m.origen_id));
      return { mes, año, miembros_aportan: unicos.size };
    });
  }

  async getSummary(
    lumisialId: string,
    /** Período a evaluar. Si se omite, la regla propia de la pantalla de Aportes. */
    periodo?: { mes: number; año: number }
  ): Promise<AporteSummary> {
    try {
      const { mes, año } = periodo ?? getDisplayPeriod();
      return await this.getSummaryForMonth(lumisialId, mes, año);
    } catch (error) {
      console.error('Error getting aportes summary:', error);
      throw error;
    }
  }
}

export const aporteService = new AporteService();
